import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
const EMAILJS_ALERT_TEMPLATE_ID =
  import.meta.env.VITE_EMAILJS_ALERT_TEMPLATE_ID ||
  import.meta.env.VITE_EMAILJS_WORKFLOW_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

// Mock function for development mode when EmailJS is not configured
const sendVerificationEmailMock = async (email, verificationCode, userName) => {
  if (import.meta.env.DEV) {
    console.info(`[AcquireOS Dev Mode] Verification email for ${email}`);
    console.info(`[AcquireOS Dev Mode] Verification code: ${verificationCode}`);
    return {
      success: true,
      delivered: false,
      message: 'Development mode: Email not actually sent. Check console for code.'
    };
  }
  return {
    success: false,
    error: 'Email delivery is not configured.'
  };
};

const hasEmailConfig = Boolean(
  EMAILJS_SERVICE_ID &&
  EMAILJS_TEMPLATE_ID &&
  EMAILJS_PUBLIC_KEY
);

const hasWorkflowEmailConfig = Boolean(
  EMAILJS_SERVICE_ID &&
  EMAILJS_ALERT_TEMPLATE_ID &&
  EMAILJS_PUBLIC_KEY
);

export const isEmailDeliveryConfigured = () => hasEmailConfig;
export const isWorkflowEmailDeliveryConfigured = () => hasWorkflowEmailConfig;

// ─── Get org profile safely (lazy-loaded to avoid circular imports) ───────────
const getOrgProfile = () => {
  try {
    const raw = localStorage.getItem('acquireos_org_profile');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

// Build org signature for email bodies
const buildOrgSignature = (org) => {
  if (!org) return '';
  const lines = [];
  if (org.senderName) lines.push(org.senderName);
  if (org.companyName) lines.push(org.companyName);
  if (org.phone) lines.push(org.phone);
  if (org.contactEmail) lines.push(org.contactEmail);
  if (org.website) lines.push(org.website);
  if (org.officeAddress) lines.push(org.officeAddress);
  return org.emailSignature || lines.join('\n');
};

// ─── Build plain-text body for OTP email ─────────────────────────────────────
const buildOtpHtmlBody = (userName, verificationCode, supportEmail) =>
`Hi ${userName || 'User'},

To authenticate your AcquireOS AI account, use the One-Time Password below:

┌─────────────────────┐
      ${verificationCode}
└─────────────────────┘

This code is valid for 10 minutes. Do not share it with anyone.

⚠️  If you did not request this code, someone may be trying to access
    your account. You can safely ignore this email — your account
    remains secure.

Need help? Contact us at ${supportEmail}

— AcquireOS AI Team`;

// ─── Build plain-text body for account status emails ─────────────────────────
const buildStatusHtmlBody = (toName, decision, reason, supportEmail, senderName) => {
  const isApproved    = decision === 'Approved';
  const isSuspended   = decision === 'Suspended';
  const isReactivated = isApproved && reason &&
    (reason.toLowerCase().includes('reinstated') || reason.toLowerCase().includes('reactivated'));

  if (isReactivated) return (
`✅  ACCOUNT REINSTATED

Hi ${toName},

Great news! Your AcquireOS account access has been reinstated by our
administrators. You can now log in and resume using the platform.

  👉  https://acquireosai.com

If you have any questions, reply to this email or contact us at
${supportEmail}

— ${senderName}`
  );

  if (isApproved) return (
`🎉  ACCOUNT APPROVED

Hi ${toName},

Congratulations! Your registration request for AcquireOS AI has been
reviewed and approved by our administrators.

You can now log in and begin your deal analysis:

  👉  https://acquireosai.com

If you have any questions, reply to this email or contact us at
${supportEmail}

— ${senderName}`
  );

  if (isSuspended) return (
`⛔  ACCOUNT SUSPENDED

Hi ${toName},

We would like to inform you that your AcquireOS account access has
been suspended.

Reason:  ${reason || 'Administrator decision.'}

If you believe this is an error, please contact us at
${supportEmail}

— ${senderName}`
  );

  // Rejected
  return (
`❌  REGISTRATION UPDATE

Hi ${toName},

Thank you for your interest in AcquireOS AI. After reviewing your
registration, we are unable to approve your account at this time.

Reason:  ${reason || 'Outside of our current target profile.'}

If you believe this is an error, please reply to this email with any
additional information, or contact us at ${supportEmail}

— ${senderName}`
  );
};

export const sendVerificationEmail = async (email, verificationCode, userName) => {
  if (!hasEmailConfig) {
    return sendVerificationEmailMock(email, verificationCode, userName);
  }

  const org = getOrgProfile();
  const supportEmail = org?.contactEmail || 'info@acquireosai.com';
  const senderName = org?.senderName || 'AcquireOS AI Team';
  const companyName = org?.companyName || 'AcquireOS AI';
  const signature = buildOrgSignature(org);

  const subject = `AcquireOS AI: Your Verification Code ${verificationCode}`;
  const emailBody = buildOtpHtmlBody(userName, verificationCode, supportEmail);

  try {
    emailjs.init({ publicKey: EMAILJS_PUBLIC_KEY });

    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      {
        subject,
        email_body: emailBody,
        to_email: email,
        to_name: userName || 'User',
        verification_code: verificationCode,
        company_name: companyName,
        support_email: supportEmail,
        sender_name: senderName,
        company_website: org?.website || '',
        email_signature: signature,
      },
      {
        publicKey: EMAILJS_PUBLIC_KEY
      }
    );

    return {
      success: true,
      delivered: true,
      message: 'Verification email sent successfully.'
    };
  } catch (error) {
    return {
      success: false,
      error: error?.text || error?.message || 'Failed to send verification email.'
    };
  }
};

const sendWorkflowAlertEmailMock = async (email, alert, dealName) => {
  if (import.meta.env.DEV) {
    console.info(`[AcquireOS Dev Mode] Workflow alert email for ${email}`);
    console.info(`[AcquireOS Dev Mode] ${alert.title}: ${alert.message}`);
    return {
      success: true,
      delivered: false,
      message: 'Development mode: workflow alert email logged to console.'
    };
  }

  return {
    success: false,
    error: 'Email delivery is not configured.'
  };
};

export const sendWorkflowAlertEmail = async ({ email, userName, alert, dealName }) => {
  if (!email || !alert) {
    return { success: false, error: 'Missing workflow alert email recipient or alert.' };
  }

  if (!hasWorkflowEmailConfig) {
    return sendWorkflowAlertEmailMock(email, alert, dealName);
  }

  const org = getOrgProfile();

  try {
    emailjs.init({ publicKey: EMAILJS_PUBLIC_KEY });

    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_ALERT_TEMPLATE_ID,
      {
        to_email: email,
        to_name: userName || 'User',
        company_name: org?.companyName || 'AcquireOS AI',
        support_email: org?.contactEmail || 'info@acquireosai.com',
        sender_name: org?.senderName || 'AcquireOS AI Team',
        company_website: org?.website || '',
        email_signature: buildOrgSignature(org),
        subject: `[${String(alert.severity || 'info').toUpperCase()}] ${org?.companyName || 'AcquireOS'}: ${alert.title}`,
        alert_title: alert.title,
        alert_message: alert.message,
        alert_severity: alert.severity,
        alert_action: alert.actionText,
        deal_name: dealName || 'Current Deal',
        module_number: alert.moduleNumber,
        sent_at: new Date().toLocaleString(),
      },
      {
        publicKey: EMAILJS_PUBLIC_KEY
      }
    );

    return {
      success: true,
      delivered: true,
      message: 'Workflow alert email sent successfully.'
    };
  } catch (error) {
    return {
      success: false,
      error: error?.text || error?.message || 'Failed to send workflow alert email.'
    };
  }
};

// ─── Generate a branded AI email body for LOI / outreach / follow-up ─────────
/**
 * Build a complete branded email body for AI-generated outreach.
 *
 * @param {object} opts
 * @param {string} opts.greeting         - e.g. "Hi John,"
 * @param {string} opts.body             - Main body paragraphs
 * @param {string} [opts.ctaText]        - Call to action text (optional)
 * @param {object} [opts.org]            - Override org profile (optional)
 * @returns {string} Full email body text
 */
export const buildBrandedEmailBody = ({ greeting, body, ctaText, org: orgOverride } = {}) => {
  const org = orgOverride || getOrgProfile();
  const sig = buildOrgSignature(org);
  const companyLine = org?.companyName ? `\n\nBest regards,\n${sig}` : '\n\nBest regards,\nThe AcquireOS AI Team';

  const parts = [];
  if (greeting) parts.push(greeting);
  if (body) parts.push(body);
  if (ctaText) parts.push(ctaText);
  parts.push(companyLine);

  return parts.join('\n\n');
};

// ─── Build an HTML email for document generation (LOI, lender outreach, etc.) ─
/**
 * Build a branded HTML email/document block.
 *
 * @param {object} opts
 * @param {string} opts.subject          - Email subject line
 * @param {string} opts.recipientName    - Recipient name
 * @param {string} opts.body             - HTML body content
 * @param {object} [opts.org]            - Override org profile (optional)
 * @returns {string} Full HTML string
 */
export const buildBrandedHtmlEmail = ({ subject, recipientName, body, org: orgOverride } = {}) => {
  const org = orgOverride || getOrgProfile();
  const brandColor = org?.brandColor || '#1B3A6B';
  const companyName = org?.companyName || 'AcquireOS AI';
  const senderName = org?.senderName || 'AcquireOS AI Team';
  const sigLines = buildOrgSignature(org).split('\n').filter(Boolean);

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <style>
    body { font-family: Arial, sans-serif; color: #1f2933; line-height: 1.6; max-width: 680px; margin: 0 auto; padding: 24px; }
    .email-header { background: ${brandColor}; color: #fff; padding: 20px 24px; border-radius: 8px 8px 0 0; }
    .email-header h1 { margin: 0; font-size: 18px; }
    .email-header p  { margin: 4px 0 0; font-size: 12px; opacity: 0.8; }
    .email-body { border: 1px solid #e5e7eb; border-top: none; padding: 24px; border-radius: 0 0 8px 8px; }
    .email-sig { border-top: 1px solid #e5e7eb; margin-top: 24px; padding-top: 16px; font-size: 12px; color: #6b7280; }
    .email-sig-bar { width: 3px; height: 60px; background: ${brandColor}; display: inline-block; vertical-align: top; margin-right: 10px; }
  </style>
</head>
<body>
  <div class="email-header">
    <h1>${companyName}</h1>
    ${org?.website ? `<p>${org.website}</p>` : ''}
  </div>
  <div class="email-body">
    ${subject ? `<h2 style="margin-top:0;color:${brandColor}">${subject}</h2>` : ''}
    ${recipientName ? `<p>Dear ${recipientName},</p>` : ''}
    ${body || ''}
    <div class="email-sig">
      <span class="email-sig-bar"></span>
      <div style="display:inline-block">
        ${sigLines.map((l, i) => `<div style="font-weight:${i === 0 ? 700 : 400};${i === 0 ? `color:${brandColor}` : 'color:#374151'}">${l}</div>`).join('')}
      </div>
    </div>
  </div>
</body>
</html>`;
};

// Export sendVerificationEmail as the main sendEmail function
export const sendEmail = sendVerificationEmail;

/**
 * Send a real-time registration approval or rejection email to the user
 * using the same EmailJS service as the OTP flow.
 *
 * @param {object} opts
 * @param {string} opts.toEmail      - Recipient email address
 * @param {string} opts.toName       - Recipient display name
 * @param {'Approved'|'Rejected'} opts.decision - Admin decision
 * @param {string} [opts.reason]     - Rejection reason (only used when decision === 'Rejected')
 */
export const sendRegistrationStatusEmail = async ({ toEmail, toName, decision, reason }) => {
  const isApproved  = decision === 'Approved';
  const isSuspended = decision === 'Suspended';
  const isReactivated = isApproved && reason &&
    (reason.toLowerCase().includes('reinstated') || reason.toLowerCase().includes('reactivated'));

  const org = getOrgProfile();
  const supportEmail = org?.contactEmail || 'info@acquireosai.com';
  const senderName   = org?.senderName   || 'AcquireOS AI Team';
  const companyName  = org?.companyName  || 'AcquireOS AI';
  const signature    = buildOrgSignature(org);

  // ── Pick subject ──────────────────────────────────────────────────────────
  const subject = isReactivated
    ? `Your ${companyName} Account Has Been Reinstated`
    : isApproved
      ? `Your ${companyName} Account Has Been Approved! 🎉`
      : isSuspended
        ? `Your ${companyName} Account Access Has Been Suspended`
        : `Update on Your ${companyName} Registration Request`;

  // ── Build styled HTML inner body ──────────────────────────────────────────
  const emailBody = buildStatusHtmlBody(toName, decision, reason, supportEmail, senderName);

  // ── Send via EmailJS ──────────────────────────────────────────────────────
  if (hasEmailConfig) {
    try {
      emailjs.init({ publicKey: EMAILJS_PUBLIC_KEY });
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          subject,
          email_body: emailBody,
          to_email: toEmail,
          to_name: toName || 'User',
          company_name: companyName,
          support_email: supportEmail,
          sender_name: senderName,
          company_website: org?.website || '',
          email_signature: signature,
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      return { success: true, delivered: true };
    } catch (error) {
      console.error('[AcquireOS Mailer] Failed to send registration status email:', error);
      return { success: false, error: error?.text || error?.message };
    }
  }

  // ── Dev mode fallback — log to console ────────────────────────────────────
  const logBgColor = isApproved ? '#10b981' : isSuspended ? '#64748b' : '#ef4444';
  console.info(
    `%c[AcquireOS Dev] Registration ${decision} Email → ${toEmail}\nSubject: ${subject}`,
    `background:${logBgColor};color:#fff;padding:4px 10px;border-radius:4px;font-size:13px;font-weight:bold;`
  );
  return { success: true, delivered: false, message: 'Dev mode: logged to console.' };
};
